import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Box, Typography, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { Activity as ActivityIcon } from 'lucide-react';
import { formatedDate, formatActivityTime } from 'utils/date';
import { ListRow, ListRowIcon } from 'components/UI';
import { useCertificateActivity, CertificateActivityLog } from 'hooks/api/useCertificateActivity';
import { useDeferredLoading } from 'hooks/useDeferredLoading';
import { CertificateStatusChip } from 'components/pages/certificates/CertificateStatusChip';
import { SearchFilterSortBar } from 'components/pages/certificates/productPage/SearchFilterSortBar';
import {
  ActivitySortDrawer,
  type AdditionalActivitySort,
  type ActivitySortDirection,
  type ActivitySortField,
} from 'components/pages/certificates/productPage/ActivitySortDrawer';
import {
  ActivityFilterDrawer,
  type ActivityFilterValues,
} from 'components/pages/certificates/productPage/ActivityFilterDrawer';
import { ActivityListSkeleton } from 'components/pages/certificates/productPage/ActivityListSkeleton';
import SortMenuDesktop from 'components/pages/certificates/SortMenuDesktop';
import SortConfigPopover from 'components/pages/certificates/SortConfigPopover';
import {
  getActivityTypeConfig,
  getSelectedGroupKeys,
} from 'components/pages/certificates/productPage/activityTypes';
import type { DateRangeKey } from 'components/pages/certificates/CertificatesDateRangeFilter';
import { PAGE_HEADER_STICKY_HEIGHT } from 'components/UI/PageHeader';

interface ActivityTabContentProps {
  certificateId: string;
}

/** Rows pulled per infinite-scroll step in the Activity timeline (VERA-978). */
/**
 * Exported so the certificate page can PREFETCH this screen's first page at the
 * same size. A prefetch at a different limit would land on a different query key
 * and warm nothing (see certificateActivityQuery).
 */
export const ACTIVITY_PAGE_SIZE = 20;

/** Orderable properties, in the order "Add sort" hands them out. */
const ACTIVITY_SORT_FIELDS: ActivitySortField[] = ['date', 'type'];

/**
 * The same two fields with their display names, for the DESKTOP field picker.
 *
 * Labels match what `SortField` resolves these keys to, so the picker menu and the
 * configure popover beside it say the same word. (The mobile sheet's own option
 * list calls `type` "Type" rather than "Event type" — a pre-existing difference
 * between two surfaces that are never on screen together, left alone here rather
 * than changed as a side effect of this work.)
 */
const ACTIVITY_SORT_MENU_FIELDS = [
  { label: 'Date', value: 'date' },
  { label: 'Event type', value: 'type' },
];

const getActorName = (log: CertificateActivityLog): string | null => {
  if (!log.user) return null;
  if (log.user.type === 'admin') return 'Vera';

  const name = [log.user.first_name, log.user.last_name].filter(Boolean).join(' ').trim();
  return name || log.user.email || null;
};

/** Inclusive start of the window a preset range covers, or null when none is set. */
const rangeStart = (range: DateRangeKey): Date | null => {
  if (!range) return null;
  const from = new Date();
  const months = range === 'last_month' ? 1 : range === 'last_3m' ? 3 : range === 'last_6m' ? 6 : 12;
  from.setMonth(from.getMonth() - months);
  return from;
};

const EmptyState: React.FC<{ title: string; body: string }> = ({ title, body }) => {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        textAlign: 'center',
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '276px',
        py: 4,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: theme => theme.palette.grey[50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          border: theme => `1px solid ${theme.palette.grey[300]}`,
        }}
      >
        <ActivityIcon size={24} color={theme.palette.text.primary} />
      </Box>
      <Typography sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5, fontSize: '16px' }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', maxWidth: 320, fontSize: '14px' }}>
        {body}
      </Typography>
    </Stack>
  );
};

export const ActivityTabContent: React.FC<ActivityTabContentProps> = ({ certificateId }) => {
  /*
    SORT IS A MENU ON DESKTOP, A BOTTOM SHEET ON A PHONE.

    Both halves were the sheet, so at 1280 clicking Sort slid a phone drawer up from
    the bottom of the screen. The certificates list had already solved this one
    surface away — `handleSortClick` there branches on `isMobile` and opens
    `SortMenuDesktop` (the field picker) or `SortConfigPopover` (once a sort exists),
    both anchored to the Sort button. This mirrors that branch exactly, with this
    screen's own fields, so the two toolbars behave the same way at the same width.

    Same reason the list keeps the sheet at `xs`: a bottom sheet is right on a phone,
    where it is thumb-reachable and a popover hanging off a 44px button is not.

    `useMediaQuery` is evaluated at render, and in this app it does not re-evaluate on
    a live resize — measure on a fresh load at the target width rather than by
    dragging the window.
  */
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('md'));

  const [query, setQuery] = useState<string>('');
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [sortOpen, setSortOpen] = useState<boolean>(false);
  const [sortStep, setSortStep] = useState<'pick' | 'configure'>('pick');
  /** Desktop only: the Sort button, as the anchor for whichever popover opens. */
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [sortConfigAnchor, setSortConfigAnchor] = useState<HTMLElement | null>(null);
  const [sortField, setSortField] = useState<ActivitySortField | null>(null);
  const [sortDirection, setSortDirection] = useState<ActivitySortDirection | null>(null);
  const [additionalSorts, setAdditionalSorts] = useState<AdditionalActivitySort[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [selectedRange, setSelectedRange] = useState<DateRangeKey>(null);

  // Same single-growing-window idiom the Scans list uses (VERA-476): the API returns
  // the top N by date for `limit: N, offset: 0`, so re-requesting with a larger N is
  // simpler than accumulating pages client-side and can't drift out of order.
  const [visibleCount, setVisibleCount] = useState<number>(ACTIVITY_PAGE_SIZE);

  const { data, isLoading, isFetching, isRefetching } = useCertificateActivity({
    certificateId,
    limit: visibleCount,
  });

  // `limit` is part of the query key, so growing the window starts a NEW query and
  // `data` is momentarily undefined. Holding the last response means the timeline stays
  // on screen while the wider page loads instead of collapsing back to a skeleton.
  const lastResponseRef = useRef<typeof data | null>(null);
  if (data) lastResponseRef.current = data;
  const response = data ?? lastResponseRef.current;

  const logs = useMemo(() => response?.data || [], [response]);
  const total = response?.total ?? 0;
  const hasMore = logs.length < total;

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      entries => {
        // Guard while a fetch is in flight: the sentinel stays mounted until the wider
        // page lands, so without this the observer queues several pages at once.
        // `isFetching` rather than `isRefetching` alone because growing the window
        // changes the query key, which reads as a first load, not a refetch.
        if (entries[0]?.isIntersecting && !isFetching && !isRefetching) {
          setVisibleCount(count => count + ACTIVITY_PAGE_SIZE);
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isFetching, isRefetching]);

  // Filtering and sorting are client-side over the fetched window, matching ScansList.
  // The window is what the reader can actually scroll to, so filtering it is the same
  // set they see; server-side filtering would need new API params (out of scope here).
  const visibleLogs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const presetFrom = rangeStart(selectedRange);
    const from = dateFrom ? new Date(dateFrom + 'T00:00:00') : presetFrom;
    const to = dateTo ? new Date(dateTo + 'T23:59:59') : null;

    const filtered = logs.filter(log => {
      if (selectedTypes.length > 0 && !selectedTypes.includes(log.type)) return false;

      if (from || to) {
        const at = new Date(log.created_at);
        if (from && at < from) return false;
        if (to && at > to) return false;
      }

      if (!normalizedQuery) return true;

      const label = getActivityTypeConfig(log.type).label.toLowerCase();
      const actor = (getActorName(log) || '').toLowerCase();
      return label.includes(normalizedQuery) || actor.includes(normalizedQuery);
    });

    // Multi-key sort, same descriptor loop ScansList runs: primary first, then each
    // additional sort as a tie-break, first non-zero comparison wins.
    const sortDescriptors: AdditionalActivitySort[] = [];
    if (sortField && sortDirection) sortDescriptors.push({ field: sortField, direction: sortDirection });
    if (additionalSorts.length) sortDescriptors.push(...additionalSorts);

    if (sortDescriptors.length === 0) return filtered;

    return [...filtered].sort((a, b) => {
      for (const { field, direction } of sortDescriptors) {
        const cmp =
          field === 'date'
            ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            : getActivityTypeConfig(a.type).label.localeCompare(
                getActivityTypeConfig(b.type).label,
                undefined,
                { sensitivity: 'base' }
              );

        if (cmp !== 0) return direction === 'ASC' ? cmp : -cmp;
      }
      return 0;
    });
  }, [
    logs,
    query,
    selectedTypes,
    dateFrom,
    dateTo,
    selectedRange,
    sortField,
    sortDirection,
    additionalSorts,
  ]);

  // One badge unit per active facet: each selected event-type GROUP counts once (not
  // its eleven expanded transfer types), plus each date facet.
  const filterBadgeCount =
    getSelectedGroupKeys(selectedTypes).length +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    (selectedRange ? 1 : 0);
  const hasSort = sortField !== null && sortDirection !== null;

  const clearSort = () => {
    setSortField(null);
    setSortDirection(null);
    setAdditionalSorts([]);
    // Clearing from inside the configure popover leaves it anchored to a sort that
    // no longer exists — and re-opening Sort should then land on the field picker,
    // which is the `hasSort` branch below. Closing it here is what makes that true.
    setSortConfigAnchor(null);
  };

  /**
   * Adopt a field as the primary sort. Direction defaults to DESC (newest first),
   * which is the default the mobile sheet already applies for this timeline — the
   * certificates list uses ASC because its default field is a NAME.
   */
  const pickPrimaryField = (field: string) => {
    setSortField(field as ActivitySortField);
    if (!sortDirection) setSortDirection('DESC');
  };

  const applyFilters = (values: ActivityFilterValues) => {
    /* No `setFilterOpen(false)` — the drawer auto-applies, so this fires 400ms
       after any field changes and closing here would dismiss the sheet on the
       user's first tap. Dismissal is `onClose`'s job. */
    setSelectedTypes(values.types || []);
    setDateFrom(values.dateFrom || '');
    setDateTo(values.dateTo || '');
    setSelectedRange(values.selectedRange ?? null);
  };

  // Skeleton only on the FIRST load, never over content already on screen (§19).
  const isFirstLoad = (isLoading || isFetching) && !lastResponseRef.current;

  /*
   * The first-load latch above was already the most correct gate of the five surfaces —
   * it just had no floor on how long it could last. Deferring it adds the missing half:
   * a first load that resolves inside 180ms now shows nothing at all instead of a
   * one-frame blink, and one that outlasts it stays up long enough to read.
   */
  const showSkeleton = useDeferredLoading(isFirstLoad);

  return (
    <Stack sx={{ pb: 1 }}>
      {/* Pins BELOW the page header, so header + toolbar read as one pinned
          block with the hairline at its bottom — the certificates page draws
          exactly that, from a single HeaderBar element. */}
      <SearchFilterSortBar
        subject='activity'
        stickyTop={PAGE_HEADER_STICKY_HEIGHT}
        query={query}
        onQueryChange={setQuery}
        filterBadgeCount={filterBadgeCount}
        onOpenFilter={() => setFilterOpen(true)}
        // One unit per active sort key, matching Scans: the primary plus each tie-break.
        sortBadgeCount={hasSort ? 1 + additionalSorts.length : 0}
        onOpenSort={e => {
          if (!isMobile) {
            // `e.currentTarget` is the Sort button itself, read synchronously —
            // the same anchor the certificates list hands its two popovers.
            const anchor = (e?.currentTarget as HTMLElement) ?? null;
            if (hasSort) setSortConfigAnchor(anchor);
            else setSortMenuAnchor(anchor);
            return;
          }
          setSortStep(hasSort ? 'configure' : 'pick');
          setSortOpen(true);
        }}
        showSortClear={hasSort}
        onClearSort={clearSort}
      />

      {/* PHONE ONLY. Gated rather than merely left closed, matching how the
          certificates list mounts its own sheet, so the desktop tree carries no
          drawer that a stray state flip could open. */}
      {isMobile && (
        <ActivitySortDrawer
          open={sortOpen}
          onClose={() => {
            setSortOpen(false);
            setSortStep('pick');
          }}
          step={sortStep}
          setStep={setSortStep}
          primaryField={sortField || 'date'}
          primaryDirection={sortDirection || 'DESC'}
          additionalSorts={additionalSorts}
          onPrimaryFieldChange={pickPrimaryField}
          onPrimaryDirectionChange={setSortDirection}
          onClearAll={clearSort}
          /* Nothing to do — the sheet auto-applies, so this runs 400ms after any
             change. Closing here would dismiss on the first field pick and
             rewinding the step would yank the user out of `configure` mid-edit;
             the sort is already committed by the handlers above. See
             `ScansTabContent` for the full note. */
          onApply={() => {}}
          /*
            ONE commit callback, fired on Apply. Previously three granular ones
            (change / remove / add) that each wrote straight to page state, so the
            list re-sorted underneath the still-open sheet.
          */
          onAdditionalChange={next => setAdditionalSorts(next)}
        />
      )}

      {/* DESKTOP: the field picker, `@repo/ui`'s `MenuButton` in controlled mode —
          literally the component `apps/platform`'s Sort renders, so row height,
          radius, hover and keyboard behaviour are not described here and cannot
          drift from it. Opens only when nothing is sorted yet; once a sort exists
          the button opens the configure popover below instead. */}
      {!isMobile && (
        <SortMenuDesktop
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={() => setSortMenuAnchor(null)}
          fields={ACTIVITY_SORT_MENU_FIELDS}
          onPickField={(field, anchor) => {
            pickPrimaryField(field);
            setSortMenuAnchor(null);
            // Hand off to the configure popover — see `SortMenuDesktop`'s
            // "The pick HANDS OFF".
            setSortConfigAnchor(anchor);
          }}
        />
      )}

      {!isMobile && (
        <SortConfigPopover
          anchorEl={sortConfigAnchor}
          open={Boolean(sortConfigAnchor)}
          onClose={() => setSortConfigAnchor(null)}
          availableFields={ACTIVITY_SORT_FIELDS}
          primaryField={sortField || 'date'}
          primaryDirection={sortDirection || 'DESC'}
          additionalSorts={additionalSorts}
          disabledFields={[
            ...(sortField ? [sortField] : []),
            ...additionalSorts.map(sort => sort.field),
          ]}
          onPrimaryFieldChange={pickPrimaryField}
          onPrimaryDirectionChange={direction => setSortDirection(direction)}
          onClearAll={clearSort}
          onChangeAdditional={(index, next) =>
            setAdditionalSorts(current =>
              current.map((sort, i) => (i === index ? (next as AdditionalActivitySort) : sort))
            )
          }
          onRemoveAdditional={index =>
            setAdditionalSorts(current => current.filter((_, i) => i !== index))
          }
          onAddAdditional={field =>
            setAdditionalSorts(current => [
              ...current,
              { field: field as ActivitySortField, direction: 'DESC' },
            ])
          }
        />
      )}

      <ActivityFilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        initialValues={{ types: selectedTypes, dateFrom, dateTo, selectedRange }}
        onApply={applyFilters}
      />

      {showSkeleton ? (
        <ActivityListSkeleton />
      ) : isFirstLoad ? null : logs.length === 0 ? (
        <EmptyState
          title='No activity yet'
          body='Activity for this certificate, like transfers and status changes, will appear here.'
        />
      ) : visibleLogs.length === 0 ? (
        // Distinct from the empty state above. There IS activity here, it is just all
        // excluded right now, so the fix is to change the search or filters.
        <EmptyState
          title='No activity found'
          body="Try adjusting your search or filters to find what you're looking for."
        />
      ) : (
        <Stack spacing={0}>
          {visibleLogs.map(log => {
            const { label, Icon } = getActivityTypeConfig(log.type);
            const actor = getActorName(log);

            return (
              // VERA-978: the shared `ListRow` — the same component the Scans list
              // renders — so the two timelines can't drift on avatar size, padding or
              // where the timestamp sits.
              //
              // No `truncate`: activity labels come from a known, finite set of phrases
              // ("Transfer cancellation requested"), so they wrap rather than clip. The
              // Scans list truncates because its label is an unbounded name/email.
              <ListRow
                key={log.id}
                // A bare glyph, no disc — but still inside the full-width leading
                // slot, so the labels stay on one rail down the list and line up
                // with the Scans list's 40px avatar.
                leading={<ListRowIcon icon={Icon} />}
                label={label}
                // Actor only. The timestamp moved out of this line into the right-hand
                // slot, where Scans already puts it. Em-dash placeholder when the event
                // has no actor, so every row stays two lines (same rule as Scans).
                sublabel={actor || '—'}
                sublabelMuted={!actor}
                // The state this event left the certificate in, in the same slot and to
                // the LEFT of the timestamp exactly as the Scans list places its scan
                // chip. Renders nothing when the log carries no status, which is the
                // common case — the component decides, so the row never shows an empty
                // pill or an "Unknown".
                meta={<CertificateStatusChip status={log.certificate_status} />}
                // Relative ("2 days ago") via the SAME helper Scans uses, flipping to an
                // absolute date past 3 days. Absolute date kept as the hover title.
                time={formatActivityTime(log.created_at)}
                timeTitle={formatedDate({ date: log.created_at, format: 'MMM do yyyy, h:mm a' })}
              />
            );
          })}
        </Stack>
      )}

      {/* Infinite scroll: a sentinel below the list pulls the next page as it comes into
          view. rootMargin starts the fetch before the sentinel is actually visible so the
          list rarely stalls. Only rendered while the server has more rows than we hold. */}
      {hasMore && !isFirstLoad && (
        <Stack ref={loadMoreRef} alignItems='center' justifyContent='center' sx={{ py: 4 }}>
          <CircularProgress size={22} />
        </Stack>
      )}
    </Stack>
  );
};

export default ActivityTabContent;
